import { ArrowRight, Compass, Layers, Mail, Phone, ShieldCheck, Users } from "lucide-react";
import React from "react";
import "./AB65001.scss";

// ============================================================================
// DATA
// ============================================================================

const VALUES = [
  {
    icon: Compass,
    title: "Build for how teams actually work",
    body: "Software should fit around real workflows, not force a team to reorganize their day around a tool. We start every product by watching how the work actually gets done.",
  },
  {
    icon: Layers,
    title: "One system beats five disconnected ones",
    body: "Most teams aren't slowed down by a missing feature — they're slowed down by data trapped across tools that don't talk to each other. We build for the seams.",
  },
  {
    icon: ShieldCheck,
    title: "Boring reliability, on purpose",
    body: "The most exciting thing our platform can do is not go down during your busiest week. We'd rather be dependable than flashy.",
  },
  {
    icon: Users,
    title: "Support from people who'll actually help",
    body: "When something breaks, you should be able to reach a person who understands your setup, not a queue. That's true at every plan tier, not just Enterprise.",
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

const AB65001: React.FC = () => {
  return (
    <div className="ab-wrap">
      {/* HERO */}
      <section className="ab-hero">
        <div className="ab-hero-inner">
          <div className="ab-kicker">
            <span className="ab-kicker-rule" />
            About Cilpron
          </div>
          <h1>
            Software for teams who are tired of
            <br />
            stitching tools together
          </h1>
          <p className="ab-hero-sub">
            Cilpron builds business software for the industries that get
            overlooked by generic SaaS — hospitality, education, and the
            operational teams who run on systems that don't talk to each
            other. We think that's a problem worth fixing properly, not
            papering over with another integration.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section className="ab-story">
        <div className="ab-story-art">
          <img
            src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=900&q=80&auto=format&fit=crop"
            alt="Team working together at a desk"
          />
        </div>
        <div className="ab-story-copy">
          <div className="ab-kicker">
            <span className="ab-kicker-rule" />
            Why we started
          </div>
          <h2>Most teams aren't missing a feature. They're missing a system.</h2>
          <p className="ab-pullquote">
            “A person whose whole job was reconciling the gaps between five
            tools that each did one thing well.”
          </p>
          <p>
            We kept running into the same pattern: a team running five
            different tools, and a person whose whole job was reconciling the
            gaps between them. Not because the tools were bad — because
            nobody built for the handoffs.
          </p>
          <p>
            Cilpron exists to build the version that doesn't need a
            reconciliation job. One system, built around how a specific
            industry actually operates, instead of a generic product
            stretched thin across everyone.
          </p>
        </div>
      </section>

      {/* VALUES */}
      <section className="ab-values">
        <div className="ab-values-head">
          <div className="ab-kicker">
            <span className="ab-kicker-rule" />
            What we believe
          </div>
          <h2>The principles behind how we build</h2>
        </div>
        <div className="ab-values-list">
          {VALUES.map((v) => (
            <div className="ab-value-row" key={v.title}>
              <span className="ab-value-icon">
                <v.icon size={20} strokeWidth={1.75} />
              </span>
              <div className="ab-value-text">
                <h3>{v.title}</h3>
                <p>{v.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT / CTA */}
      <section className="ab-contact">
        <div className="ab-contact-inner">
          <div className="ab-contact-copy">
            <h2>Curious whether your team's gaps are ones we'd actually fix?</h2>
            <p>
              Walk us through how your team works today — the handoffs, the
              spreadsheets, the tools that don't talk to each other — and
              we'll tell you honestly whether we're the right system for
              that, or not.
            </p>
            <a className="ab-btn-primary" href="mailto:support@cilpron.com">
              Tell us how you work <ArrowRight size={16} />
            </a>
          </div>
          <div className="ab-contact-details">
            <a className="ab-contact-item" href="tel:+2348122221040">
              <span className="ab-contact-icon">
                <Phone size={17} strokeWidth={1.75} />
              </span>
              <span>
                <span className="ab-contact-label">Call us</span>
                <span className="ab-contact-value">+234 812 222 1040</span>
              </span>
            </a>
            <a className="ab-contact-item" href="mailto:support@cilpron.com">
              <span className="ab-contact-icon">
                <Mail size={17} strokeWidth={1.75} />
              </span>
              <span>
                <span className="ab-contact-label">Email us</span>
                <span className="ab-contact-value">support@cilpron.com</span>
              </span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AB65001;